import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import { cn } from "./lib/utils";
import { UIComponentContextError } from "./errors/ui-component.errors";
import { UnderlineTabsComponentNames } from "./errors/ui-component-error-messages.constants";

interface UnderlineTabsContextValue {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
}

const UnderlineTabsContext = createContext<UnderlineTabsContextValue | null>(
  null,
);

function useUnderlineTabsContext(): UnderlineTabsContextValue {
  const context = useContext(UnderlineTabsContext);
  if (context === null) {
    throw new UIComponentContextError(
      UnderlineTabsComponentNames.UNDERLINE_TABS_TRIGGER,
      UnderlineTabsComponentNames.UNDERLINE_TABS,
    );
  }
  return context;
}

export interface UnderlineTabsProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function UnderlineTabs({
  value,
  onValueChange,
  children,
  className,
}: UnderlineTabsProps): React.JSX.Element {
  const contextValue = useMemo(
    () => ({ value, onValueChange }),
    [value, onValueChange],
  );

  return (
    <UnderlineTabsContext.Provider value={contextValue}>
      <div data-slot="underline-tabs" className={className}>
        {children}
      </div>
    </UnderlineTabsContext.Provider>
  );
}

export interface UnderlineTabsListProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

export function UnderlineTabsList({
  children,
  className,
  testId = "tabs-list",
}: UnderlineTabsListProps): React.JSX.Element {
  const listRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Called for the missing-provider guard it throws; the list renders nothing
  // from the context itself. Also subscribes the list to selection changes, in
  // case a caller ever memoizes it away from its owner's re-render.
  useUnderlineTabsContext();

  const measure = useCallback((): void => {
    const list = listRef.current;
    if (list === null) return;

    const active = list.querySelector<HTMLElement>('[data-state="active"]');
    const left = active?.offsetLeft ?? 0;
    const width = active?.offsetWidth ?? 0;
    setIndicator((previous) =>
      previous.left === left && previous.width === width
        ? previous
        : { left, width },
    );
  }, []);

  // Every render, not selection change alone: a trigger's own label can change
  // width without resizing the list box the observer below watches (PLA-1758),
  // and returning `previous` unchanged is what stops that from looping.
  useEffect(measure);

  // The triggers are observed as well as the list: a font swap or async label
  // content resizes a trigger without resizing the list box. Re-subscribing on
  // every render is what keeps that set current as triggers come and go.
  useEffect((): (() => void) | undefined => {
    const list = listRef.current;
    if (list === null || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const trigger of Array.from(
      list.querySelectorAll<HTMLElement>('[role="tab"]'),
    )) {
      observer.observe(trigger);
    }
    return (): void => {
      observer.disconnect();
    };
  });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>): void => {
      const list = listRef.current;
      if (list === null) return;

      const tabs = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
      );
      const currentIndex = tabs.findIndex(
        (tab) => tab === document.activeElement,
      );

      if (currentIndex === -1) return;

      let nextIndex: number | undefined;

      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex !== undefined) {
        const nextTab = tabs[nextIndex];
        if (nextTab !== undefined) {
          event.preventDefault();
          nextTab.focus();
        }
      }
    },
    [],
  );

  return (
    <nav
      ref={listRef}
      data-slot="underline-tabs-list"
      className={cn(
        "relative flex shrink-0 items-center gap-2 h-8 px-3 border-b border-border bg-background overflow-x-auto scrollbar-hide",
        className,
      )}
      data-testid={testId}
      role="tablist"
      onKeyDown={handleKeyDown}
    >
      {/* One rule that travels, rather than a border toggling between triggers —
          the movement is the feedback that the tab changed. Width and offset are
          measured from the live DOM because the labels are translated and their
          widths are not knowable ahead of time. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-0.5 bg-foreground motion-safe:transition-[transform,width] motion-safe:duration-300 motion-safe:ease-out"
        style={{
          width: `${indicator.width}px`,
          transform: `translateX(${indicator.left}px)`,
          opacity: indicator.width === 0 ? 0 : 1,
        }}
      />
      {children}
    </nav>
  );
}

export interface UnderlineTabsTriggerProps {
  readonly value: string;
  readonly children: React.ReactNode;
  readonly badge?: number | undefined;
  readonly className?: string;
  readonly testId?: string;
  readonly "data-testid"?: string;
}

export function UnderlineTabsTrigger({
  value,
  children,
  badge,
  className,
  testId,
  "data-testid": dataTestId,
}: UnderlineTabsTriggerProps): React.JSX.Element {
  const context = useUnderlineTabsContext();
  const isActive = context.value === value;

  const displayContent =
    badge !== undefined && badge > 0 ? (
      <>
        {children} ({badge})
      </>
    ) : (
      children
    );

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={(): void => {
        context.onValueChange(value);
      }}
      className={cn(
        "px-2 py-1 text-sm h-full transition-colors whitespace-nowrap shrink-0",
        isActive
          ? "font-semibold text-foreground"
          : "font-medium text-muted-foreground hover:text-foreground",
        className,
      )}
      data-testid={dataTestId ?? testId ?? `tab-${value}`}
      data-state={isActive ? "active" : "inactive"}
    >
      {displayContent}
    </button>
  );
}

export interface UnderlineTabsContentProps {
  readonly value: string;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly "data-testid"?: string;
}

export const UnderlineTabsContent = React.forwardRef<
  HTMLDivElement,
  UnderlineTabsContentProps
>(function UnderlineTabsContent(
  { value, children, className, "data-testid": testId },
  ref,
) {
  const context = useUnderlineTabsContext();

  if (context.value !== value) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-slot="underline-tabs-content"
      role="tabpanel"
      data-state="active"
      data-testid={testId}
      className={cn("outline-none", className)}
    >
      {children}
    </div>
  );
});
